import classnames from 'classnames/bind'
import React from 'react'
import { useTranslate } from 'react-polyglot'

import FakeArrowLink from '~/components/FakeArrowLink'
import ImageWithPlaceholder, {
  ImageWithPlaceholderProps,
} from '~/components/ImageWithPlaceholder'
import Link, { LinkProps } from '~/components/Link'
import Ratio from '~/components/Ratio'
import RichText, { PrismicRichText, RichTextBlock } from '~/components/RichText'

import { useStyle } from '~/providers/StyleProvider'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ArticleItemProps {
  className?: string
  title?: RichTextBlock[]
  text?: RichTextBlock[]
  date?: string
  image?: ImageWithPlaceholderProps
  link?: LinkProps
  big?: boolean
}

function ArticleItem({
  className,
  link,
  image,
  date,
  title,
  text,
  big,
}: ArticleItemProps) {
  const subtitleStyle = useStyle({
    textPreset: big ? 'title-heading-5-section' : 'title-heading-5',
  })
  const textStyle = useStyle({ textPreset: 'text-paragraph' })
  const dateStyle = useStyle({
    textPreset: 'text-caption',
    color: 'wild-blue-yonder',
  })

  const t = useTranslate()

  return (
    link && (
      <Link {...link} className={cx(css.ArticleItem, className, { big })}>
        <div className={css.imageContainer}>
          <div className={cx(css.imageContent)}>
            {image && (
              <>
                {!big ? (
                  <Ratio
                    className={cx(css.ratioImage, css.hideOnSmallScreen)}
                    ratio={309 / 200}>
                    {(cn) => (
                      <ImageWithPlaceholder
                        sizesFromBreakpoints={[
                          { breakpoint: 'md', ratio: 24 / 48 },
                          { ratio: 18 / 24 },
                        ]}
                        className={css.image}
                        layout="fill"
                        objectFit="cover"
                        {...image}
                      />
                    )}
                  </Ratio>
                ) : (
                  <ImageWithPlaceholder
                    className={cx(css.coverImageBig, css.image)}
                    sizesFromBreakpoints={[
                      { breakpoint: 'md', ratio: 32 / 48 },
                      { ratio: 18 / 24 },
                    ]}
                    layout="fill"
                    objectFit="cover"
                    {...image}
                  />
                )}
                <Ratio
                  className={cx(css.ratioImage, css.hideOnLargeScreen)}
                  ratio={279 / 200}>
                  {(cn) => (
                    <ImageWithPlaceholder
                      sizesFromBreakpoints={[
                        { breakpoint: 'md', ratio: 24 / 48 },
                        { ratio: 18 / 24 },
                      ]}
                      className={css.image}
                      layout="fill"
                      objectFit="cover"
                      {...image}
                    />
                  )}
                </Ratio>
              </>
            )}
          </div>
        </div>
        <div className={css.content}>
          <div className={css.texts}>
            {date && <div className={cx(css.date, dateStyle)}>{date}</div>}
            {title && (
              <RichText
                className={cx(subtitleStyle, css.subtitle)}
                render={title}
              />
            )}

            {text && (
              <RichText className={cx(textStyle, css.text)} render={title} />
            )}
          </div>
          <FakeArrowLink className={css.cta}>
            <span>{t('article.read_more')}</span>
          </FakeArrowLink>
        </div>
      </Link>
    )
  )
}

ArticleItem.defaultProps = {}

export default ArticleItem
